import React, { useEffect, useState, useCallback } from 'react';
import fonts from '../data/fonts.json';
import CustomPalettes from './CustomPalettes';
import EditCustomPalette from './EditCustomPalette';
import DeletePalette from './DeletePalette';
import { getUserCustomPalettes } from '../services/customPalettes';
import { Trash2, Edit3 } from 'lucide-react';

const StylesLeftCustom = ({ userChoices, setUserChoices, onSaveAndContinue }) => {
  const [showAllCustomPalettes, setShowAllCustomPalettes] = useState(false);
  const [showCustomPalettesModal, setShowCustomPalettesModal] = useState(false);
  const [showEditPaletteModal, setShowEditPaletteModal] = useState(false);
  const [customPalettes, setCustomPalettes] = useState([]);
  const [loadingCustomPalettes, setLoadingCustomPalettes] = useState(true);
  const [deletePaletteId, setDeletePaletteId] = useState(null);
  const [editingPalette, setEditingPalette] = useState(null);
  
  const handlePaletteChange = useCallback((palette, isCustom = true) => {
    if (isCustom) {
      setUserChoices((prev) => ({ 
        ...prev, 
        theme: palette.theme,
        palette: palette.name,
        isCustomPalette: true,
        customPaletteId: palette._id
      }));
    }
  }, [setUserChoices]);

  // Load custom palettes when component mounts (only once)
  useEffect(() => {
    const fetchCustomPalettes = async () => {
      try {
        setLoadingCustomPalettes(true);
        const palettes = await getUserCustomPalettes();
        setCustomPalettes(palettes);
      } catch (error) {
        console.error('Error loading custom palettes:', error);
      } finally {
        setLoadingCustomPalettes(false);
      }
    };

    fetchCustomPalettes();
  }, []); // Empty dependency array - only run once on mount

  // Separate effect to handle initial palette selection when userChoices change
  useEffect(() => {
    // Only auto-select if we have palettes loaded and no custom palette is selected
    if (customPalettes.length > 0 && (!userChoices.isCustomPalette || !userChoices.customPaletteId)) {
      handlePaletteChange(customPalettes[0], true);
    }
  }, [customPalettes, userChoices.isCustomPalette, userChoices.customPaletteId, handlePaletteChange]);

  const handleDeviceChange = (device) => {
    setUserChoices((prev) => ({ ...prev, device }));
  };

  const handleFontChange = (event) => {
    const font = event.target.value;
    setUserChoices((prev) => ({ ...prev, font }));
  };

  const handleCustomPaletteSave = (newPalette) => {
    setShowCustomPalettesModal(false);
    setCustomPalettes([...customPalettes, newPalette]);
    
    // Automatically select the new palette
    handlePaletteChange(newPalette, true);
  };

  const handleEditClick = (palette, e) => {
    e.stopPropagation(); // Prevent triggering the onClick of the parent div
    setEditingPalette(palette);
    setShowEditPaletteModal(true);
  };

  const handleEditSave = (updatedPalette) => {
    setShowEditPaletteModal(false);
    setEditingPalette(null);
    
    // Update the palette in the list
    const updatedPalettes = customPalettes.map(palette => 
      palette._id === updatedPalette._id ? updatedPalette : palette
    );
    setCustomPalettes(updatedPalettes);
    
    // If the updated palette is currently selected, force update the user choices
    // This ensures StylesRight.js gets the updated palette data
    if (userChoices.isCustomPalette && userChoices.customPaletteId === updatedPalette._id) {
      // Force a re-render by updating userChoices with new data
      setUserChoices((prev) => ({
        ...prev,
        theme: updatedPalette.theme,
        palette: updatedPalette.name,
        // Add a timestamp to force StylesRight.js to refetch
        lastUpdated: Date.now()
      }));
    }
  };

  const handleDeleteClick = (paletteId, e) => {
    e.stopPropagation(); // Prevent triggering the onClick of the parent div
    setDeletePaletteId(paletteId);
  };

  const handleDeleteConfirm = (paletteId) => {
    // Remove the palette from the list
    const updatedPalettes = customPalettes.filter(palette => palette._id !== paletteId);
    setCustomPalettes(updatedPalettes);
    
    // If the deleted palette was selected, check if we already have stored colors
    if (userChoices.isCustomPalette && userChoices.customPaletteId === paletteId) {
      // If we have stored colors (customPaletteColors), we can keep using this palette
      if (userChoices.customPaletteColors) {
        // Keep using the same palette (it's already saved in the project)
        console.log('Keeping deleted palette colors from project storage');
      } 
      // Otherwise select another palette if available
      else if (updatedPalettes.length > 0) {
        handlePaletteChange(updatedPalettes[0], true);
      } else {
        // Reset to default if no custom palettes left
        setUserChoices((prev) => ({ 
          ...prev, 
          isCustomPalette: false,
          customPaletteId: null
        }));
      }
    }
  };

  return (
    <div className="h-full p-4 overflow-y-auto">
      {/* Device and Font Selection */}
      <div className="flex flex-wrap md:flex-nowrap items-center mb-6 w-full max-w-xl mx-auto">
        {/* Device Selection */}
        <div className="w-full md:w-1/2 mb-4 md:mb-0">
          <h2 className="text-lg font-semibold mb-2">Design for:</h2>
          <div className="flex space-x-4">
            <button
              onClick={() => handleDeviceChange('Desktop')}
              className={`px-4 py-2 rounded-md ${
                userChoices.device === 'Desktop'
                  ? 'bg-black text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              Desktop
            </button>
            <button
              onClick={() => handleDeviceChange('Mobile')}
              className={`px-4 py-2 rounded-md ${
                userChoices.device === 'Mobile'
                  ? 'bg-black text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              Mobile
            </button>
          </div>
        </div>

        {/* Font Family Selection */}
        <div className="w-full md:w-1/2 md:pl-2">
          <h2 className="text-lg font-semibold mb-2">Use Font:</h2>
          <select
            value={userChoices.font}
            onChange={handleFontChange}
            className="w-full px-4 py-2 pr-6 rounded-md bg-gray-200 text-gray-700"
            style={{
              fontFamily: fonts.find((font) => font.name === userChoices.font)?.family,
            }}
          >
            {fonts.map((font) => (
              <option
                key={font.name}
                value={font.name}
                style={{ fontFamily: font.family }}
              >
                {font.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Custom Palettes Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2">Custom Palettes:</h2>
        <div className="mb-4">
          <button
            onClick={() => setShowCustomPalettesModal(true)}
            className="px-4 py-2 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300"
          >
            Create New Palette
          </button>
        </div>

        {/* Display all custom palettes */}
        {loadingCustomPalettes ? (
          <div className="flex justify-center py-4">
            <p className="text-gray-500">Loading custom palettes...</p>
          </div>
        ) : customPalettes.length === 0 ? (
          <div className="mt-4 p-4 bg-gray-100 rounded-md text-center">
            <p className="text-gray-500">You haven't created any custom palettes yet.</p>
            <p className="text-gray-500">Click "Create New Palette" to get started.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Display limited or all custom palettes based on showAllCustomPalettes */}
            {(showAllCustomPalettes ? customPalettes : customPalettes.slice(0, 3)).map(palette => {
              const isSelected = userChoices.isCustomPalette && userChoices.customPaletteId === palette._id;
              const isLightTheme = palette.theme === 'light';
              const borderClass = isSelected
                ? isLightTheme
                  ? 'border-2 border-black' // Selected in light theme
                  : 'border-2 border-pink-500' // Selected in dark theme
                : isLightTheme
                  ? 'border border-gray-300' // Unselected in light theme
                  : 'border border-gray-700'; // Unselected in dark theme
              const textColor = isLightTheme ? 'text-black' : 'text-white';
              const bgColor = isLightTheme ? 'bg-white' : 'bg-gray-800';
              const iconColor = isLightTheme ? 'text-gray-500' : 'text-gray-400';
              const editIconHoverColor = isLightTheme ? 'hover:text-blue-600' : 'hover:text-blue-400';
              const deleteIconHoverColor = isLightTheme ? 'hover:text-red-600' : 'hover:text-red-400';
              
              return (
                <div 
                  key={palette._id}
                  className={`flex items-center p-2 rounded-md cursor-pointer ${borderClass} ${bgColor} ${textColor}`}
                  onClick={() => handlePaletteChange(palette, true)}
                >
                  <div className="flex space-x-2 mr-4">
                    <div 
                      className="w-6 h-6 rounded" 
                      style={{ backgroundColor: palette.palette.primary }}
                    ></div>
                    <div 
                      className="w-6 h-6 rounded" 
                      style={{ backgroundColor: palette.palette.secondary }}
                    ></div>
                    <div 
                      className="w-6 h-6 rounded" 
                      style={{ backgroundColor: palette.palette.accent }}
                    ></div>
                  </div>
                  <span className="flex-1">{palette.name}</span>
                  
                  {/* Action buttons container */}
                  <div className="flex items-center space-x-1">
                    {/* Edit button */}
                    <button
                      onClick={(e) => handleEditClick(palette, e)}
                      className={`p-2 rounded-full hover:bg-opacity-20 ${isLightTheme ? 'hover:bg-blue-100' : 'hover:bg-blue-900'} transition-colors`}
                      aria-label="Edit palette"
                    >
                      <Edit3 className={`w-5 h-5 ${iconColor} ${editIconHoverColor}`} />
                    </button>
                    
                    {/* Delete button */}
                    <button
                      onClick={(e) => handleDeleteClick(palette._id, e)}
                      className={`p-2 rounded-full hover:bg-opacity-20 ${isLightTheme ? 'hover:bg-red-100' : 'hover:bg-red-900'} transition-colors`}
                      aria-label="Delete palette"
                    >
                      <Trash2 className={`w-5 h-5 ${iconColor} ${deleteIconHoverColor}`} />
                    </button>
                  </div>
                </div>
              );
            })}
            
            {/* Show more button for custom palettes */}
            {!showAllCustomPalettes && customPalettes.length > 3 && (
              <button
                onClick={() => setShowAllCustomPalettes(true)}
                className="mt-2 text-blue-500 underline"
              >
                Show more color palettes
              </button>
            )}
          </div>
        )}
      </div>

      <button
        onClick={onSaveAndContinue}
        className="w-full px-4 py-3 mt-2 mb-4 bg-black text-white rounded-md hover:bg-gray-800"
      >
        Save and Continue
      </button>

      {/* Custom Palettes Modal */}
      {showCustomPalettesModal && (
        <CustomPalettes 
          onClose={() => setShowCustomPalettesModal(false)} 
          onSave={handleCustomPaletteSave} 
        />
      )}

      {/* Edit Custom Palette Modal */}
      {showEditPaletteModal && editingPalette && (
        <EditCustomPalette
          palette={editingPalette}
          onClose={() => {
            setShowEditPaletteModal(false);
            setEditingPalette(null);
          }}
          onSave={handleEditSave}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletePaletteId && (
        <DeletePalette
          paletteId={deletePaletteId}
          onClose={() => setDeletePaletteId(null)}
          onDelete={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default StylesLeftCustom;